import { useState, type MouseEvent } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { useNavigate } from 'react-router';
import type { Employee } from '@/data/employees/interfaces';
import { EditEmailDialog } from '@/packages/employer/components/edit-email-dialog';
import { RemoveEmployee } from '@/packages/employer/components/remove-employee-dialog';
import { ResetEmployeePassword } from '@/packages/employer/components/reset-password-dialog';

interface EmployeeMenuProps {
  employee: Employee;
}

export const EmployeeMenu = ({ employee }: EmployeeMenuProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showAddOrEditEmployeeDialog, setAddOrEditEmployeeDialog] =
    useState(false);
  const [showRemoveEmployeeDialog, setShowRemoveEmployeeDialog] =
    useState(false);
  const [showResetEmployeePasswordDialog, setShowResetEmployeePasswordDialog] =
    useState(false);

  const deletedUser = employee?.deleted_at !== null;

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    setAddOrEditEmployeeDialog(true);
    handleClose();
  };

  const handleRemove = () => {
    setShowRemoveEmployeeDialog(true);
    handleClose();
  };

  const handleResetPassword = () => {
    setShowResetEmployeePasswordDialog(true);
    handleClose();
  };

  return (
    <>
      <Stack direction="row" spacing={1}>
        <Box>
          <IconButton
            color="inherit"
            disabled={deletedUser}
            aria-label="Abrir menu"
            onClick={handleOpen}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            elevation={5}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              onClick={() =>
                navigate(
                  `/empresa/gestao-de-colaboradores/editar/${employee?.id}`
                )
              }
            >
              Editar
            </MenuItem>
            <MenuItem onClick={handleEdit}>Alterar email</MenuItem>
            <MenuItem onClick={handleResetPassword}>Redefinir senha</MenuItem>
            <MenuItem onClick={handleRemove}>Excluir usuário</MenuItem>
          </Menu>
        </Box>
      </Stack>
      {showAddOrEditEmployeeDialog && (
        <EditEmailDialog
          employee={employee}
          onClose={() => setAddOrEditEmployeeDialog(false)}
        />
      )}
      {showRemoveEmployeeDialog && (
        <RemoveEmployee
          employeeId={employee?.id}
          onClose={() => setShowRemoveEmployeeDialog(false)}
        />
      )}
      {showResetEmployeePasswordDialog && (
        <ResetEmployeePassword
          employee={employee}
          onClose={() => setShowResetEmployeePasswordDialog(false)}
        />
      )}
    </>
  );
};
